import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ExitReadinessGauge from '../components/ExitReadiness/ExitReadinessGauge';
import {
  getLeadAssessmentResults,
  downloadLeadAssessmentPDF
} from '../services/leadAssessmentApi';
import './ExitReadinessResults.css';

const ExitReadinessResults = () => {
  const { sessionToken } = useParams();
  const navigate = useNavigate();

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [backgroundLoaded, setBackgroundLoaded] = useState(false);

  // Preload background image asynchronously
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setBackgroundLoaded(true);
    };
    img.src = `${process.env.PUBLIC_URL}/Background.png`;
  }, []);

  useEffect(() => {
    if (sessionToken) {
      loadResults();
    } else {
      setError('Invalid session token');
      setLoading(false);
    }
  }, [sessionToken]);

  const loadResults = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await getLeadAssessmentResults(sessionToken);

      if (!result.success) {
        throw new Error(result.error);
      }

      setResults(result.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading results:', err);
      setError(err.message || 'Failed to load results');
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloadingPDF(true);
      await downloadLeadAssessmentPDF(sessionToken);
      setDownloadingPDF(false);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF. Please try again.');
      setDownloadingPDF(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="results-loading">
        <div className="loading-spinner" />
        <p>Loading your results...</p>
      </div>
    );
  }

  // Error state
  if (error || !results) {
    return (
      <div className="results-error">
        <h2>Unable to Load Results</h2>
        <p>{error || 'Results not found'}</p>
        <button onClick={() => navigate('/exit-readiness-assessment')}>
          Start New Assessment
        </button>
      </div>
    );
  }

  const { assessment, readiness_level, recommendations, lead_info } = results;
  const scores = {
    overall: assessment.overall_exit_readiness_score,
    business: assessment.business_readiness_score,
    personal: assessment.personal_readiness_score
  };

  // Helper function to get score color based on gap zones
  const getScoreColor = (score) => {
    if (score > 86) return '#a07d5a';      // No gaps - bronze/tan
    if (score > 72) return '#6ba86e';      // Minor gaps - green
    if (score > 57) return '#6ca5d6';      // Considerable gaps - blue
    if (score > 43) return '#d4c563';      // Critical gaps - yellow
    if (score > 28) return '#d9a066';      // Very critical gaps - orange
    return '#d97373';                       // Extremely critical - red
  };

  // Get responses grouped by category (include N/A answers for display)
  const businessResponses = assessment.responses?.filter(r => r.category === 'business') || [];
  const personalResponses = assessment.responses?.filter(r => r.category === 'personal') || [];

  return (
    <div
      className="exit-readiness-results"
      style={{
        backgroundImage: backgroundLoaded ? `url(${process.env.PUBLIC_URL}/Background.png)` : 'none',
        backgroundColor: backgroundLoaded ? 'transparent' : '#1a3a2e',
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        transition: 'background-image 0.3s ease-in'
      }}
    >
      {/* Logo at top */}
      <div className="results-logo-container">
        <img
          src={`${process.env.PUBLIC_URL}/Login Logo.png`}
          alt="Dominion Business Advisors"
          className="results-logo"
        />
      </div>

      <div className="results-container">
        {/* Header Section */}
        <div className="results-header">
          <div className="header-content">
            <h1 className="results-title">Your Exit Readiness Assessment</h1>
            {lead_info && (
              <p className="results-subtitle">
                {lead_info.full_name} • {lead_info.business_name}
              </p>
            )}
          </div>
          <button
            className="download-pdf-btn"
            onClick={handleDownloadPDF}
            disabled={downloadingPDF}
          >
            {downloadingPDF ? (
              <>
                <span className="btn-spinner" />
                Generating PDF...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download PDF Report
              </>
            )}
          </button>
        </div>

        {/* Introduction & Purpose Section */}
        <div className="results-section context-section">
          <h2>Introduction & Purpose</h2>

          <h3 style={{ marginTop: '1.5rem', marginBottom: '0.75rem' }}><strong>Why This Assessment Matters:</strong></h3>
          <p>
            Every business owner will exit their business—the only question is whether that exit happens on your terms or someone else's. Whether through a strategic sale, family succession, management buyout, or an unexpected life event, the transition of your business represents one of the most significant financial and personal decisions you will ever make. Yet most business owners spend more time planning a two-week vacation than preparing for the day they will leave their life's work.
          </p>
          <p style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
            This Exit Readiness Assessment Overview provides you with a clear, honest snapshot of where you stand today across the critical dimensions that determine whether your eventual exit will be successful, fulfilling, and aligned with your vision for the future.
          </p>

          <h3 style={{ marginTop: '1.5rem', marginBottom: '0.75rem' }}><strong>What Is Exit Planning?</strong></h3>
          <p>
            Exit planning is the intentional, strategic process of preparing both you and your business for a future transition—regardless of when or how that transition occurs. It is not simply "selling your business." Rather, it is a comprehensive approach that addresses three fundamental questions:
          </p>
          <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem', marginTop: '0.75rem' }}>
            <li><strong>What do you want?</strong> — Clarifying your personal, financial, and legacy goals for life after business ownership.</li>
            <li><strong>What do you have?</strong> — Understanding the true value of your business and your personal wealth position today.</li>
            <li><strong>What's the gap?</strong> — Identifying the distance between where you are and where you need to be—and creating a plan to close it.</li>
          </ul>
          <p>
            Effective exit planning recognizes that your business is likely your largest asset, yet it is also your least liquid one. Without proper preparation, owners often discover too late that their business cannot fund the life they envisioned, or that the transition leaves relationships, legacy, and purpose unfulfilled.
          </p>

          <h3 style={{ marginTop: '1.5rem', marginBottom: '0.75rem' }}><strong>The Value Acceleration Methodology:</strong></h3>
          <p>
            This assessment is built upon the Value Acceleration Methodology—a proven framework used by Certified Exit Planning Advisors (CEPAs) to help business owners systematically increase the value, transferability, and readiness of their businesses while simultaneously preparing themselves and their families for transition.
          </p>
          <p style={{ marginTop: '0.75rem' }}>The methodology focuses on three critical areas:</p>
          <ul style={{ marginLeft: '1.5rem', marginBottom: '1rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>The Three Gaps Framework</strong> helps you identify and address the critical distances between where your business stands today and where it needs to be to achieve your financial and personal goals. These gaps (related to business value, personal wealth, and operational profitability) are often larger than owners realize, but they are also closable with the right strategy and sufficient time.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>The Four Intangible Capitals framework</strong> examines the human expertise, documented systems, customer relationships, and market reputation that make your business valuable independent of your personal involvement. These capitals determine whether a buyer sees your company as a risky, owner-dependent operation or as a well-structured enterprise capable of thriving under new ownership.
            </li>
            <li style={{ marginBottom: '0.75rem' }}>
              <strong>The Five Ds framework</strong> ensures you have considered and prepared for involuntary transitions before they occur. Owners who have addressed the Five Ds protect their families from crisis decision-making, preserve the value they have built, and maintain control even when circumstances seem beyond their control.
            </li>
          </ul>

          <div style={{ background: '#f3f4f6', padding: '1.5rem', borderRadius: '8px', marginTop: '1.5rem' }}>
            <h4 style={{ marginBottom: '0.75rem' }}><strong>A Stewardship Perspective:</strong></h4>
            <p>
              At Dominion Business Advisors, we believe that business ownership is a form of stewardship. The enterprise you have built represents not only financial value but also the livelihoods of employees, the trust of customers, and the opportunity to leave a meaningful legacy for future generations.
            </p>
            <p style={{ marginTop: '0.75rem' }}>
              Preparing well for your exit is not merely a financial exercise—it is an act of wisdom, responsibility, and care for those who depend on what you have built.
            </p>
          </div>
        </div>

        {/* Overall Score Section */}
        <div className="results-section overall-score-section">
          <div className="section-content">
            <div className="gauge-container">
              <ExitReadinessGauge
                score={scores.overall}
                size={250}
                strokeWidth={25}
                showLabel={true}
                label="Overall Exit Readiness Score"
                readinessLevel={readiness_level}
                animated={true}
              />
            </div>
            <div className="readiness-description">
              <h2>
                {readiness_level.label}
              </h2>
              <p>{readiness_level.description}</p>
              <div className="score-breakdown">
                <div className="breakdown-item">
                  <span className="breakdown-label">Business Readiness</span>
                  <div className="breakdown-bar-container">
                    <div
                      className="breakdown-bar business"
                      style={{ width: `${scores.business}%` }}
                    />
                    <span className="breakdown-value">{Math.round(scores.business)}%</span>
                  </div>
                </div>
                <div className="breakdown-item">
                  <span className="breakdown-label">Personal Readiness</span>
                  <div className="breakdown-bar-container">
                    <div
                      className="breakdown-bar personal"
                      style={{ width: `${scores.personal}%` }}
                    />
                    <span className="breakdown-value">{Math.round(scores.personal)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benchmarks Section */}
        <div className="results-section benchmarks-section">
          <h2>Understanding Your Score</h2>
          <div className="score-explanation">
            <h3>Assessment Score Scaling (17% - 100%)</h3>
            <p className="scoring-intro">
              Assessment scores range from <strong>17%</strong> (minimum) to <strong>100%</strong> (maximum).
              Your score represents where your business falls within the valuation range based on readiness factors.
            </p>
            <div className="scoring-scale">
              <div className="scale-point">
                <span className="scale-percentage">17%</span>
                <span className="scale-label">Low End (0%)</span>
                <p>Beginning journey, significant preparation needed</p>
              </div>
              <div className="scale-point">
                <span className="scale-percentage">58%</span>
                <span className="scale-label">Average (49%)</span>
                <p>Midpoint of valuation range, moderate readiness</p>
              </div>
              <div className="scale-point highlight">
                <span className="scale-percentage">67%</span>
                <span className="scale-label">Green Zone (60%)</span>
                <p>Well positioned for exit, strong fundamentals</p>
              </div>
              <div className="scale-point highlight">
                <span className="scale-percentage">72%</span>
                <span className="scale-label">Best-in-Class (66%)</span>
                <p>Elite level readiness, optimal exit positioning</p>
              </div>
              <div className="scale-point">
                <span className="scale-percentage">100%</span>
                <span className="scale-label">High End (100%)</span>
                <p>Maximum valuation potential achieved</p>
              </div>
            </div>
            <p className="scoring-note">
              <strong>Linear scaling:</strong> A 58% assessment represents an average business at approximately
              49% of your valuation range. Scores of 67% and above place you in the top tier of businesses ready for exit.
            </p>
          </div>
        </div>

        {/* Individual Question Scores Section */}
        {(businessResponses.length > 0 || personalResponses.length > 0) && (
          <div className="results-section">
            <h2>Your Detailed Assessment Responses</h2>

            {businessResponses.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ marginBottom: '1rem', color: '#1e40af' }}>Business Readiness Questions</h3>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {businessResponses.map((response, index) => {
                    const isNA = response.answer_value === 0;
                    return (
                      <div key={index} style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: '1rem',
                        padding: '0.75rem',
                        background: isNA ? '#e5e7eb' : '#f9fafb',
                        borderRadius: '6px',
                        alignItems: 'center'
                      }}>
                        <div>
                          <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.5' }}>{response.question_text}</p>
                        </div>
                        <div style={{
                          minWidth: '60px',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          backgroundColor: isNA ? '#9ca3af' : getScoreColor(response.score),
                          color: '#ffffff',
                          fontWeight: '600',
                          textAlign: 'center',
                          fontSize: '1rem'
                        }}>
                          {isNA ? 'N/A' : `${Math.round(response.score)}%`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {personalResponses.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ marginBottom: '1rem', color: '#1e40af' }}>Personal Readiness Questions</h3>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  {personalResponses.map((response, index) => {
                    const isNA = response.answer_value === 0;
                    return (
                      <div key={index} style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: '1rem',
                        padding: '0.75rem',
                        background: isNA ? '#e5e7eb' : '#f9fafb',
                        borderRadius: '6px',
                        alignItems: 'center'
                      }}>
                        <div>
                          <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.5' }}>{response.question_text}</p>
                        </div>
                        <div style={{
                          minWidth: '60px',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          backgroundColor: isNA ? '#9ca3af' : getScoreColor(response.score),
                          color: '#ffffff',
                          fontWeight: '600',
                          textAlign: 'center',
                          fontSize: '1rem'
                        }}>
                          {isNA ? 'N/A' : `${Math.round(response.score)}%`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Valuation Section */}
        <div className="results-section valuation-section">
          <h2>Business Valuation Context</h2>
          <div className="valuation-summary">
            <div className="valuation-metric">
              <span className="metric-label">Annual Revenue</span>
              <span className="metric-value">
                ${assessment.annual_revenue?.toLocaleString() || '—'}
              </span>
            </div>
            <div className="valuation-metric">
              <span className="metric-label">EBITDA</span>
              <span className="metric-value">
                ${assessment.ebitda_amount?.toLocaleString() || '—'}
              </span>
            </div>
            {assessment.industry && (
              <div className="valuation-metric">
                <span className="metric-label">Industry</span>
                <span className="metric-value">{assessment.industry}</span>
              </div>
            )}
            {assessment.valuation_multiple && (
              <div className="valuation-metric">
                <span className="metric-label">EBITDA Multiple</span>
                <span className="metric-value">{parseFloat(assessment.valuation_multiple).toFixed(1)}x</span>
              </div>
            )}
          </div>

          {assessment.valuation_low && assessment.valuation_median && assessment.valuation_high ? (
            <div className="valuation-range-section">
              <h3>Estimated Valuation Range</h3>
              <div className="valuation-range">
                <div className="range-bar">
                  <div className="range-marker low">
                    <span className="marker-label">Low</span>
                    <span className="marker-value">${assessment.valuation_low.toLocaleString()}</span>
                  </div>
                  <div className="range-line"></div>
                  <div className="range-marker median">
                    <span className="marker-label">Median</span>
                    <span className="marker-value">${assessment.valuation_median.toLocaleString()}</span>
                  </div>
                  <div className="range-line"></div>
                  <div className="range-marker high">
                    <span className="marker-label">High</span>
                    <span className="marker-value">${assessment.valuation_high.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <p className="range-explanation">
                {assessment.valuation_multiple
                  ? `Based on ${parseFloat(assessment.valuation_multiple).toFixed(1)}x EBITDA multiple applied to your ${assessment.ebitda_amount?.toLocaleString()} EBITDA.`
                  : `Based on typical ${assessment.industry} industry multiples applied to your EBITDA.`
                }
              </p>
            </div>
          ) : null}

          <p className="valuation-note">
            * This is a simplified estimate for context only. A comprehensive valuation requires detailed financial analysis.
          </p>
        </div>

        {/* Wealth Gap Section (if exists) */}
        {assessment.wealth_gap_amount && assessment.wealth_gap_amount > 0 && (
          <div className="results-section wealth-gap-section">
            <h2>Wealth Gap Analysis</h2>
            <div className="wealth-gap-content">
              <div className="wealth-gap-visual">
                <div className="gap-amount">
                  <span className="gap-label">Wealth Gap to Fill</span>
                  <span className="gap-value">
                    ${Math.abs(assessment.wealth_gap_amount).toLocaleString()}
                  </span>
                </div>
                <p className="gap-description">
                  This is the amount your business needs to provide in <strong>after-tax proceeds</strong> to reach your wealth goal.
                </p>
                <p className="gap-explanation">
                  Your current personal net worth (outside the business) is ${assessment.current_net_worth?.toLocaleString() || '0'}.
                  To reach your target of ${assessment.desired_exit_proceeds?.toLocaleString()}, your business exit
                  must generate <strong>${Math.abs(assessment.wealth_gap_amount).toLocaleString()} in after-tax proceeds</strong> to
                  close the gap.
                </p>
              </div>
              <div className="wealth-gap-details">
                <div className="detail-row">
                  <span>Your Wealth Goal:</span>
                  <span>${assessment.desired_exit_proceeds?.toLocaleString()}</span>
                </div>
                <div className="detail-row">
                  <span>Current Personal Net Worth (Outside Business):</span>
                  <span>${assessment.current_net_worth?.toLocaleString() || '0'}</span>
                </div>
                <div className="detail-row highlight">
                  <span>Required After-Tax Proceeds from Business:</span>
                  <span>${Math.abs(assessment.wealth_gap_amount).toLocaleString()}</span>
                </div>
              </div>
              <p className="wealth-gap-note">
                <strong>Note:</strong> This analysis assumes you'll need after-tax proceeds from your business exit
                to bridge the gap between your current net worth and your wealth goal. Tax planning and deal structure
                can significantly impact the final outcome.
              </p>
            </div>
          </div>
        )}

        {/* Recommendations Section */}
        <div className="results-section recommendations-section">
          <h2>Personalized Recommendations</h2>
          <p className="recommendations-intro">
            Based on your assessment results, here are your top priority action items:
          </p>
          <div className="recommendations-list">
            {recommendations.map((rec, index) => (
              <div key={index} className="recommendation-card">
                <div className="recommendation-header">
                  <div className="recommendation-icon">
                    <span className="priority-badge">Priority {index + 1}</span>
                  </div>
                  <h3>{rec.title}</h3>
                </div>
                <p className="recommendation-message">{rec.message}</p>
                <ul className="action-items">
                  {rec.action_items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="results-section cta-section">
          <div className="cta-content">
            <h2>Ready to Take the Next Step?</h2>
            <p>
              Schedule a complimentary consultation with Dominion Business Advisors to create your personalized exit roadmap.
            </p>
            <div className="cta-buttons">
              <a
                href="https://calendly.com/cameronteich/strategy-and-advisory-session"
                target="_blank"
                rel="noopener noreferrer"
                className="cta-btn primary"
              >
                Schedule Consultation
              </a>
              <a
                href="mailto:info@dominionbusinessadvisors.com"
                className="cta-btn secondary"
              >
                Email Us
              </a>
            </div>
            <p className="cta-tagline">
              We specialize in helping business owners maximize value and achieve successful exits.
            </p>
          </div>
        </div>

        {/* Copyright Footer */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem', marginBottom: '2rem' }}>
          <div className="results-footer">
            © 2025 Dominion Business Advisors LLC. All Rights Reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExitReadinessResults;
